
import React, { useEffect, useState, useCallback } from 'react';
import DocumentHeader from '../../../../documents/components/DocumentHeader';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TablePagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import AmountCard from '../../../../documents/components/AmountCard';
import CommonAccountTable from '../../../../documents/components/CommonAccountTable';
import { AccountTableColumn } from '../../../../types';
import { request } from '../../../../../../utils/request';
import { useDebounce } from '../../../../../../utils/useDebounce';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteConfirmDialog from '../../../../../../componentsUiBlock/DeleteConfirmDialog';
import { calculateInvoiceTotals } from '../../../../../../utils/invoiceCalculations';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { usePermissions } from '../../../../../../utils/usePermissions';

const ListPurchaseInvoice = () => {
  const { canDeletePurchaseInvoice } = usePermissions();
  const [loadingGst, setLoadingGst] = useState(true);
  const [loadingNonGst, setLoadingNonGst] = useState(true);
  const [gstInvoices, setGstInvoices] = useState<any[]>([]);
  const [nonGstInvoices, setNonGstInvoices] = useState<any[]>([]);

  // Pagination state for GST
  const [gstPage, setGstPage] = useState(0);
  const [gstRowsPerPage, setGstRowsPerPage] = useState(10);
  const [gstTotal, setGstTotal] = useState(0);

  // Pagination state for NON-GST
  const [nonGstPage, setNonGstPage] = useState(0);
  const [nonGstRowsPerPage, setNonGstRowsPerPage] = useState(10);
  const [nonGstTotal, setNonGstTotal] = useState(0);

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  // Filters state
  const [searchGst, setSearchGst] = useState('');
  const [searchNonGst, setSearchNonGst] = useState('');
  const [statusFilterGst, setStatusFilterGst] = useState<string>('all');
  const [statusFilterNonGst, setStatusFilterNonGst] = useState<string>('all');
  const [dateRangeGst, setDateRangeGst] = useState<string>('all');
  const [dateRangeNonGst, setDateRangeNonGst] = useState<string>('all');
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [customDateDialogFor, setCustomDateDialogFor] = useState<'gst' | 'nonGst'>('gst');
  const [loadingCombined, setLoadingCombined] = useState(false);

  const navigate = useNavigate();

  const { gstEnabled } = useGstMode();

  // Debounced searches
  const debouncedSearchGst = useDebounce(searchGst, 500);
  const debouncedSearchNonGst = useDebounce(searchNonGst, 500);

  // State for card totals
  const [gstCardTotals, setGstCardTotals] = useState({
    totalSales: 0,
    paidAmount: 0,
    unpaidAmount: 0
  });

  const [nonGstCardTotals, setNonGstCardTotals] = useState({
    totalSales: 0,
    paidAmount: 0,
    unpaidAmount: 0
  });

  const [combinedCardTotals, setCombinedCardTotals] = useState({
    totalSales: 0,
    paidAmount: 0,
    unpaidAmount: 0
  });

  // Fetch GST invoices
  const fetchGstInvoices = useCallback(async (isForCards = false) => {
    if (!isForCards) setLoadingGst(true);

    try {
      const params: Record<string, any> = {
        page: isForCards ? 1 : gstPage + 1,
        limit: isForCards ? 1000 : gstRowsPerPage,
        gstType: 'GST'
      };

      if (debouncedSearchGst) params.search = debouncedSearchGst;
      if (statusFilterGst && statusFilterGst !== 'all') params.status = statusFilterGst;

      // Handle date range
      if (dateRangeGst === 'custom' && startDate && endDate && customDateDialogFor === 'gst') {
        params.startDate = startDate.toISOString().split('T')[0];
        params.endDate = endDate.toISOString().split('T')[0];
      } else if (dateRangeGst && dateRangeGst !== 'all' && dateRangeGst !== 'custom') {
        params.dateRange = dateRangeGst;
      }

      const res = await request({
        url: 'purchase/get-all-purchase-invoices',
        method: 'get',
        params
      });

      if (res.success && res.data) {
        if (isForCards) {
          return res.data;
        } else {
          setGstInvoices(res.data);
          setGstTotal(res.pagination?.total || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching GST invoices:', error);
      if (!isForCards) {
        toast.error('Failed to fetch GST invoices');
      }
    } finally {
      if (!isForCards) setLoadingGst(false);
    }
    return [];
  }, [gstPage, gstRowsPerPage, debouncedSearchGst, statusFilterGst, dateRangeGst, startDate, endDate, customDateDialogFor]);

  // Fetch NON-GST invoices
  const fetchNonGstInvoices = useCallback(async (isForCards = false) => {
    if (!gstEnabled) {
      if (!isForCards) setLoadingNonGst(true);

      try {
        const params: Record<string, any> = {
          page: isForCards ? 1 : nonGstPage + 1,
          limit: isForCards ? 1000 : nonGstRowsPerPage,
          gstType: 'NON-GST'
        };

        if (debouncedSearchNonGst) params.search = debouncedSearchNonGst;
        if (statusFilterNonGst && statusFilterNonGst !== 'all') params.status = statusFilterNonGst;

        // Handle date range
        if (dateRangeNonGst === 'custom' && startDate && endDate && customDateDialogFor === 'nonGst') {
          params.startDate = startDate.toISOString().split('T')[0];
          params.endDate = endDate.toISOString().split('T')[0];
        } else if (dateRangeNonGst && dateRangeNonGst !== 'all' && dateRangeNonGst !== 'custom') {
          params.dateRange = dateRangeNonGst;
        }

        const res = await request({
          url: 'purchase/get-all-purchase-invoices',
          method: 'get',
          params
        });

        if (res.success && res.data) {
          if (isForCards) {
            return res.data;
          } else {
            setNonGstInvoices(res.data);
            setNonGstTotal(res.pagination?.total || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching NON-GST invoices:', error);
        if (!isForCards) {
          toast.error('Failed to fetch NON-GST invoices');
        }
      } finally {
        if (!isForCards) setLoadingNonGst(false);
      }
    }
    return [];
  }, [nonGstPage, nonGstRowsPerPage, debouncedSearchNonGst, statusFilterNonGst, dateRangeNonGst, startDate, endDate, customDateDialogFor, gstEnabled]);


  const fetchCombinedTotals = useCallback(async () => {
    setLoadingCombined(true);
    try {
      // Fetch GST totals
      const gstParams: Record<string, any> = {
        page: 1,
        limit: 1000,
        gstType: 'GST'
      };

      // Fetch NON-GST totals if not in GST-only mode
      const nonGstParams: Record<string, any> = {
        page: 1,
        limit: 1000,
        gstType: 'NON-GST'
      };

      const [gstRes, nonGstRes] = await Promise.all([
        !gstEnabled ? request({
          url: 'purchase/get-all-purchase-invoices',
          method: 'get',
          params: gstParams
        }) : Promise.resolve({ success: false, data: [] }),

        !gstEnabled ? request({
          url: 'purchase/get-all-purchase-invoices',
          method: 'get',
          params: nonGstParams
        }) : Promise.resolve({ success: false, data: [] })
      ]);

      let allInvoices: any[] = [];

      if (gstEnabled) {
        // If GST-only mode, only get GST invoices
        const gstResult = await request({
          url: 'purchase/get-all-purchase-invoices',
          method: 'get',
          params: { page: 1, limit: 1000, gstType: 'GST' }
        });

        if (gstResult.success && gstResult.data) {
          allInvoices = gstResult.data;
        }
      } else {
        // Combine GST and NON-GST invoices
        if (gstRes.success && gstRes.data) {
          allInvoices = [...allInvoices, ...gstRes.data];
        }
        if (nonGstRes.success && nonGstRes.data) {
          allInvoices = [...allInvoices, ...nonGstRes.data];
        }
      }

      if (allInvoices.length > 0) {
        const totals = calculateInvoiceTotals(allInvoices);
        setCombinedCardTotals(totals);
      }
    } catch (error) {
      console.error('Error fetching combined totals:', error);
    } finally {
      setLoadingCombined(false);
    }
  }, [gstEnabled]);

  // Add this effect to fetch combined totals when component mounts or filters change
  useEffect(() => {
    fetchCombinedTotals();
  }, [fetchCombinedTotals]);

  // Add this for combined amount cards at the top
  const combinedAmountCards = [
    {
      label: "Total Purchase",
      amount: combinedCardTotals.totalSales,
      active: false,
      onClick: () => {
        // Reset both filters to 'all' to show all invoices
        setStatusFilterGst('all');
        setStatusFilterNonGst('all');
      }
    },
    {
      label: "Total Paid",
      amount: combinedCardTotals.paidAmount,
      active: false,
      onClick: () => {
        // Set both filters to 'PAID'
        setStatusFilterGst('PAID');
        setStatusFilterNonGst('PAID');
      }
    },
    {
      label: "Total Unpaid",
      amount: combinedCardTotals.unpaidAmount,
      active: false,
      onClick: () => {
        // Set both filters to 'UNPAID'
        setStatusFilterGst('UNPAID');
        setStatusFilterNonGst('UNPAID');
      }
    }
  ];

  // Main data fetch for tables
  useEffect(() => {
    fetchGstInvoices(false);
    if (!gstEnabled) {
      fetchNonGstInvoices(false);
    }
  }, [fetchGstInvoices, fetchNonGstInvoices, gstEnabled]);

  // Fetch data for cards
  useEffect(() => {
    const fetchCardData = async () => {
      const [gstCardInvoices, nonGstCardInvoices] = await Promise.all([
        fetchGstInvoices(true),
        gstEnabled ? [] : fetchNonGstInvoices(true)
      ]);

      if (gstCardInvoices.length > 0) {
        const gstTotals = calculateInvoiceTotals(gstCardInvoices);
        setGstCardTotals(gstTotals);
      }

      if (!gstEnabled && nonGstCardInvoices.length > 0) {
        const nonGstTotals = calculateInvoiceTotals(nonGstCardInvoices);
        setNonGstCardTotals(nonGstTotals);
      }
    };

    fetchCardData();
  }, [statusFilterGst, statusFilterNonGst, gstEnabled, fetchGstInvoices, fetchNonGstInvoices]);

  // Handle page change for GST
  const handleGstPageChange = (event: unknown, newPage: number) => {
    setGstPage(newPage);
  };

  // Handle rows per page change for GST
  const handleGstRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setGstRowsPerPage(newRowsPerPage);
    setGstPage(0);
  };

  // Handle page change for NON-GST
  const handleNonGstPageChange = (event: unknown, newPage: number) => {
    setNonGstPage(newPage);
  };

  // Handle rows per page change for NON-GST
  const handleNonGstRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setNonGstRowsPerPage(newRowsPerPage);
    setNonGstPage(0);
  };

  // Handle search for GST
  const handleGstSearch = (value: string) => {
    setSearchGst(value);
    setGstPage(0);
  };

  // Handle search for NON-GST
  const handleNonGstSearch = (value: string) => {
    setSearchNonGst(value);
    setNonGstPage(0);
  };

  // Handle date range change for GST
  const handleGstDateRangeChange = (range: string) => {
    if (range === 'custom') {
      setCustomDateDialogFor('gst');
      setCustomDateDialog(true);
    } else {
      setDateRangeGst(range);
      if (range !== 'custom') {
        setStartDate(null);
        setEndDate(null);
      }
      setGstPage(0);
    }
  };

  // Handle date range change for NON-GST
  const handleNonGstDateRangeChange = (range: string) => {
    if (range === 'custom') {
      setCustomDateDialogFor('nonGst');
      setCustomDateDialog(true);
    } else {
      setDateRangeNonGst(range);
      if (range !== 'custom') {
        setStartDate(null);
        setEndDate(null);
      }
      setNonGstPage(0);
    }
  };

  // Handle custom date range click
  const handleCustomDateClick = (type: 'gst' | 'nonGst') => {
    setCustomDateDialogFor(type);
    setCustomDateDialog(true);
  };

  // Handle custom date apply
  const handleApplyCustomDates = () => {
    if (startDate && endDate) {
      if (customDateDialogFor === 'gst') {
        setDateRangeGst('custom');
        setGstPage(0);
      } else {
        setDateRangeNonGst('custom');
        setNonGstPage(0);
      }
      setCustomDateDialog(false);
    } else {
      toast.warning('Please select both start and end dates');
    }
  };

  // Handle filter change from amount cards for GST
  const handleGstCardFilterClick = (filterType: string) => {
    setGstPage(0);

    switch (filterType) {
      case 'totalSales':
        setStatusFilterGst('all');
        break;
      case 'paid':
        setStatusFilterGst('PAID');
        break;
      case 'unpaid':
        setStatusFilterGst('UNPAID');
        break;
      default:
        setStatusFilterGst('all');
    }
  };

  // Handle filter change from amount cards for NON-GST
  const handleNonGstCardFilterClick = (filterType: string) => {
    setNonGstPage(0);

    switch (filterType) {
      case 'totalSales':
        setStatusFilterNonGst('all');
        break;
      case 'paid':
        setStatusFilterNonGst('PAID');
        break;
      case 'unpaid':
        setStatusFilterNonGst('UNPAID');
        break;
      default:
        setStatusFilterNonGst('all');
    }
  };

  const CustomDatePicker = ({ label, value, onChange, minDate }: any) => (
    <DatePicker
      label={label}
      value={value}
      onChange={(newValue: any) => {
        onChange(newValue as Date | null);
      }}
      slotProps={{
        textField: {
          fullWidth: true,
          size: "small"
        }
      }}
      minDate={minDate}
    />
  );

  // GST amount cards
  const gstAmountCards = [
    {
      label: "GST Purchase",
      amount: gstCardTotals.totalSales,
      active: statusFilterGst === 'all',
      onClick: () => handleGstCardFilterClick('totalSales')
    },
    {
      label: "GST Paid",
      amount: gstCardTotals.paidAmount,
      active: statusFilterGst === 'PAID',
      onClick: () => handleGstCardFilterClick('paid')
    },
    {
      label: "GST Unpaid",
      amount: gstCardTotals.unpaidAmount,
      active: statusFilterGst === 'UNPAID',
      onClick: () => handleGstCardFilterClick('unpaid')
    }
  ];

  // NON-GST amount cards
  const nonGstAmountCards = [
    {
      label: "Non-GST Purchase",
      amount: nonGstCardTotals.totalSales,
      active: statusFilterNonGst === 'all',
      onClick: () => handleNonGstCardFilterClick('totalSales')
    },
    {
      label: "Non-GST Paid",
      amount: nonGstCardTotals.paidAmount,
      active: statusFilterNonGst === 'PAID',
      onClick: () => handleNonGstCardFilterClick('paid')
    },
    {
      label: "Non-GST Unpaid",
      amount: nonGstCardTotals.unpaidAmount,
      active: statusFilterNonGst === 'UNPAID',
      onClick: () => handleNonGstCardFilterClick('unpaid')
    }
  ];

  // Custom date range dialog with date-fns
  const renderCustomDateDialog = () => (
    <Dialog
      open={customDateDialog}
      onClose={() => {
        setCustomDateDialog(false);
        // If no dates are selected, reset to all dates
        if (!startDate || !endDate) {
          if (customDateDialogFor === 'gst') {
            setDateRangeGst('all');
          } else {
            setDateRangeNonGst('all');
          }
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Select Custom Date Range for {customDateDialogFor === 'gst' ? 'GST' : 'Non-GST'} Invoices
        <IconButton
          aria-label="close"
          onClick={() => {
            setCustomDateDialog(false);
            // If no dates are selected, reset to all dates
            if (!startDate || !endDate) {
              if (customDateDialogFor === 'gst') {
                setDateRangeGst('all');
              } else {
                setDateRangeNonGst('all');
              }
            }
          }}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mt: 1,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <Box sx={{ flex: 1 }}>
              <CustomDatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomDatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate || undefined}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setCustomDateDialog(false);
          if (customDateDialogFor === 'gst') {
            setDateRangeGst('all');
          } else {
            setDateRangeNonGst('all');
          }
          setStartDate(null);
          setEndDate(null);
        }}>
          Cancel
        </Button>
        <Button
          onClick={handleApplyCustomDates}
          variant="contained"
          disabled={!startDate || !endDate}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Delete handlers
  const handleDeleteClick = (invoice: any) => {
    setItemToDelete(invoice);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoadingDelete(true);
    try {
      const res = await request({
        url: `purchase/delete-purchase-invoice/${itemToDelete._id}`,
        method: 'delete',
      });

      if (res.success) {
        setOpenDeleteDialog(false);
        toast.success(res.message || 'Invoice deleted successfully');
        // Refresh both tables
        await Promise.all([
          fetchGstInvoices(false),
          !gstEnabled && fetchNonGstInvoices(false)
        ]);
        setItemToDelete(null);
      } else {
        toast.error(res.message || 'Failed to delete invoice');
      }
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      toast.error(error || 'Failed to delete invoice');
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  };

  // Columns definition
  const columns: AccountTableColumn[] = [
    {
      id: 'purchaseDate',
      label: 'DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.purchaseDate) return 'N/A';
        try {
          return new Date(row.purchaseDate).toLocaleDateString('en-GB');
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    { id: 'purchaseNumber', label: 'PURCHASE INVOICE NUMBER', minWidth: 150 },
    {
      id: 'party',
      label: 'PARTY/VENDOR NAME',
      minWidth: 200,
      renderCell: (row) => {
        if (row.party && typeof row.party === 'object') {
          return row.party.partyName || row.party.nickName || 'N/A';
        }
        if (row.vendor && typeof row.vendor === 'object') {
          return row.vendor.vendorName || row.vendor.vendorName || 'N/A';
        }
        return 'N/A';
      }
    },
    {
      id: 'dueDate',
      label: 'DUE DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.dueDate) return 'N/A';
        try {
          const dueDate = new Date(row.dueDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          dueDate.setHours(0, 0, 0, 0);

          const diffTime = dueDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays > 0) return `${diffDays} Days`;
          if (diffDays === 0) return 'Today';
          return `${Math.abs(diffDays)} Days Ago`;
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    {
      id: 'totalAmount',
      label: 'AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.totalAmount || 0)}
          </span>

          <span style={{ fontSize: '12px', color: '#666' }}>
            Unpaid : {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.balanceAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'status',
      label: 'STATUS',
      minWidth: 120,
      renderCell: (row) => {
        const status = row.status || 'UNPAID';
        const statusColors: Record<string, 'success' | 'warning' | 'primary' | 'info'> = {
          PAID: 'success',
          UNPAID: 'warning',
          PARTIAL_PAID: 'info',
          OVERPAID: 'primary'
        };

        return (
          <Chip
            label={status}
            color={statusColors[status as keyof typeof statusColors] || 'default'}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      minWidth: 120,
      align: 'center',
      renderCell: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 0.8 }}>
          {row?.paymentReferences?.length > 0 ? (
            <Tooltip title="Invoice locked — payment has been recorded." arrow placement='top'>
              <Box sx={{ minWidth: '49%' }}>
                <Button
                  disabled
                  variant="text"
                  fullWidth
                  sx={{
                    minWidth: 'auto',
                    fontSize: '0.60rem',
                    textWrap: 'nowrap',
                    py: 0.1,
                    px: 2,
                    borderRadius: 5,
                    border: '.8px solid #7f5ee666',
                    '&:hover': { backgroundColor: '#7f5ee666' }
                  }}
                >
                  Edit
                </Button>
              </Box>
            </Tooltip>
          ) : (<Button
            variant="text"
            fullWidth
            onClick={() => navigate(`/accounts/purchases/purchaseInvoice/updatePurchaseInvoice/${row._id}`)}
            sx={{
              minWidth: 'auto',
              fontSize: '0.60rem',
              textWrap: 'nowrap',
              py: 0.1,
              px: 2,
              borderRadius: 5,
              border: '.8px solid #7f5ee666',
              '&:hover': { backgroundColor: '#7f5ee666' }
            }}
          >
            Edit
          </Button>)}
          {canDeletePurchaseInvoice() && (
            <Button
              size="small"
              fullWidth
              variant="text"
              color="error"
              onClick={() => handleDeleteClick(row)}
              sx={{
                minWidth: 'auto',
                fontSize: '0.60rem',
                py: 0.1,
                px: 2,
                borderRadius: 5,
                border: '.8px solid #ff4d6e5e',
                '&:hover': { backgroundColor: '#ff4d6e5e' }
              }}
            >
              Delete
            </Button>
          )}

        </Box>
      )
    }
  ];

  if (loadingGst && gstInvoices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader heading='Purchase Invoices' />

      {/* Combined Total Cards at the Top */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <Accordion>
          <Tooltip title="View Overall Total Amounts" followCursor placement='top' arrow componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'black',
                color: '#fff',
                fontSize: '0.75rem',
              },
            },
            arrow: {
              sx: {
                color: 'black',
              },
            },
          }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{
                '&:hover': {
                  backgroundColor: '#ccc'
                }
              }}
            >
              <Typography variant='h6'>Overall Summary</Typography>
            </AccordionSummary>
          </Tooltip>
          <AccordionDetails>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {combinedAmountCards.map((card, index) => (
                  <AmountCard
                    key={`combined-${index}`}
                    label={card.label}
                    amount={card.amount}
                    active={card.active}
                    onClick={card.onClick}
                  />
                ))}
              </Box>
              {loadingCombined && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
                  Loading summary...
                </Typography>
              )}
            </CardContent>
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* GST Invoices Section */}
      <Box sx={{ mb: 0 }}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <Accordion>
            <Tooltip title="View GST Purchases Total Amounts" followCursor placement='top' arrow componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'black',
                  color: '#fff',
                  fontSize: '0.75rem',
                },
              },
              arrow: {
                sx: {
                  color: 'black',
                },
              },
            }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  '&:hover': {
                    backgroundColor: '#ccc'
                  }
                }}
              >
                <Typography variant="h6" color="primary">
                  GST Purchase Invoices
                  <Chip
                    label={`${gstTotal} `}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                </Typography>
              </AccordionSummary>
            </Tooltip>
            <AccordionDetails>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                </Box>

                {/* GST Amount Cards */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 0 }}>
                  {gstAmountCards.map((card, index) => (
                    <AmountCard
                      key={`gst-${index}`}
                      label={card.label}
                      amount={card.amount}
                      active={card.active}
                      onClick={card.onClick}
                    />
                  ))}
                </Box>
              </CardContent>
            </AccordionDetails>
          </Accordion>
        </Card>

        {/* GST Table */}
        <CommonAccountTable
          columns={columns}
          data={gstInvoices}
          loading={loadingGst}
          searchPlaceholder="Search GST purchase invoices..."
          searchValue={searchGst}
          onSearch={handleGstSearch}
          showSearchBar={true}
          showDateRange={true}
          selectedDateRange={dateRangeGst}
          onDateRangeChange={handleGstDateRangeChange}
          startDate={customDateDialogFor === 'gst' ? startDate : null}
          endDate={customDateDialogFor === 'gst' ? endDate : null}
          onCustomDateClick={() => handleCustomDateClick('gst')}
          showFilter={false}
          createButtonProps={{
            label: 'Create GST purchase Invoice',
            navigateTo: '/accounts/purchases/purchaseInvoice/createPurchaseInvoice?gstType=GST'
          }}
          idField='_id'
          getRowLink={(row: any) => `/accounts/purchases/purchaseInvoice/${row?._id}`}
        />

        {/* GST Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={gstTotal}
          rowsPerPage={gstRowsPerPage}
          page={gstPage}
          onPageChange={handleGstPageChange}
          onRowsPerPageChange={handleGstRowsPerPageChange}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': {
              minHeight: 52,
              padding: 0,
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
            }
          }}
        />
      </Box>

      {/* NON-GST Invoices Section (only when not in GST-only mode) */}
      {!gstEnabled && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <Accordion>
                <Tooltip title="View NON-GST Purchase Total Amounts" followCursor placement='top' arrow componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'black',
                      color: '#fff',
                      fontSize: '0.75rem',
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'black',
                    },
                  },
                }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{
                      '&:hover': {
                        backgroundColor: '#ccc'
                      }
                    }}
                  >
                    <Typography variant="h6" color="secondary.contrastText">
                      Non-GST Purchase Invoices
                      <Chip
                        label={`${nonGstTotal} `}
                        size="small"

                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.7rem', color: "secondary.contrastText" }}
                      />
                    </Typography>
                  </AccordionSummary>
                </Tooltip>
                <AccordionDetails>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    </Box>
                    {/* NON-GST Amount Cards */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 0 }}>
                      {nonGstAmountCards.map((card, index) => (
                        <AmountCard
                          key={`nongst-${index}`}
                          label={card.label}
                          amount={card.amount}
                          active={card.active}
                          onClick={card.onClick}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </AccordionDetails>
              </Accordion>
            </Card>

            {/* NON-GST Table */}
            <CommonAccountTable
              columns={columns}
              data={nonGstInvoices}
              loading={loadingNonGst}
              searchPlaceholder="Search Non-GST Purchase invoices..."
              searchValue={searchNonGst}
              onSearch={handleNonGstSearch}
              showSearchBar={true}
              showDateRange={true}
              selectedDateRange={dateRangeNonGst}
              onDateRangeChange={handleNonGstDateRangeChange}
              startDate={customDateDialogFor === 'nonGst' ? startDate : null}
              endDate={customDateDialogFor === 'nonGst' ? endDate : null}
              onCustomDateClick={() => handleCustomDateClick('nonGst')}
              showFilter={false}
              createButtonProps={{
                label: 'Create NON-GST Purchase Invoice',
                navigateTo: '/accounts/purchases/purchaseInvoice/createPurchaseInvoice?gstType=NON-GST'
              }}
              idField='_id'
              getRowLink={(row: any) => `/accounts/purchases/purchaseInvoice/${row?._id}`}
            />

            {/* NON-GST Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={nonGstTotal}
              rowsPerPage={nonGstRowsPerPage}
              page={nonGstPage}
              onPageChange={handleNonGstPageChange}
              onRowsPerPageChange={handleNonGstRowsPerPageChange}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '.MuiTablePagination-toolbar': {
                  minHeight: 52,
                  padding: 0,
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                }
              }}
            />
          </Box>
        </>
      )}

      {/* Custom Date Dialog */}
      {renderCustomDateDialog()}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        loading={loadingDelete}
        title="Delete Purchase Invoice"
        description={
          <>
            Are you sure you want to delete the purchase invoice{' '}
            <strong>"{itemToDelete?.purchaseNumber} - {(itemToDelete?.party && typeof itemToDelete?.party === 'object') ? itemToDelete?.party?.partyName : itemToDelete?.vendor?.vendorName}"</strong>?
            <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
              This action cannot be undone. All associated data will be permanently removed.
            </Alert>
          </>
        }
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ListPurchaseInvoice;